import { withRouter } from 'react-router-dom';
import { MdHub } from 'react-icons/md';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { Container, Icon } from 'rsuite';
import { FiltersArea, ResultsButton, ComparingChannelHeader, ElementsRelatedToPhysicalChannelChart, ModalUnderstandBetter, Detailing } from './components';
import Header from '../../../../components/Header';
import { useOmnichannelStore } from './store/ModuloOmnichannel.store';
import styles from './ModuloOmnichannel.module.scss';
import { PageBlocker } from '../InfoPanel/Components/PageBlocker';
import { PAGE_BLOCKER, SPEAK_WITH_SPECIALIST_TITLE } from './constants';
import { omnichannelFormSchema, type OmnichannelFormSchema } from './omnichannelFormSchema';
import { ModalSpeakWithSpecialist } from '../../../../components/ModalSpeakWithSpecialist';
import { User } from '../../../../@types';
import { useOmnichannelGaugeCharts } from './hooks/useOmnichannelGaugeCharts';
import { useGetOmnichannelDetails } from './hooks/useGetOmnichannelDetails';
import { LoadingSpinerLocal } from '../../../../components/LoadingSpinner';

type ModuloOmnichannelProps = {
  usuario: User;
};

export const ModuloOmnichannel = ({ usuario }: ModuloOmnichannelProps) => {
  const omnichannelDetailsData = useOmnichannelStore(state => state.omnichannelDetailsData);
  const omnichannelGaugeChartsData = useOmnichannelStore(state => state.omnichannelGaugeChartsData);
  const omnichannelRequestBody = useOmnichannelStore(state => state.omnichannelRequestBody);
  const showModalUnderstandBetterOmnichannel = useOmnichannelStore(state => state.showModalUnderstandBetterOmnichannel);
  const showModalSpeakWithSpecialistOmnichannel = useOmnichannelStore(state => state.showModalSpeakWithSpecialistOmnichannel);
  const setOmnichannelState = useOmnichannelStore(state => state.setOmnichannelState);

  const { omnichannelGaugeChartsLoading, handleGetOmnichannelGaugeCharts } = useOmnichannelGaugeCharts();

  const { handleDetailsPageChange, isLoadingOmnichannelDetails } = useGetOmnichannelDetails();

  // const HAS_NO_PERMISSION = !usuario?.tipoAdicionalContratos?.PAN?.includes('MODULO_OMNICHANNEL');

  // Permissão para FULL e FLEX temporária
  const HAS_NO_PERMISSION = usuario?.servicoPanel?.pacotePainel?.name !== 'FULL' && usuario?.servicoPanel?.pacotePainel?.name !== 'LIMIT';

  const methods = useForm<OmnichannelFormSchema>({
    defaultValues: {
      tipoLocalizacao: 'TODO_TERRITORIO',
      produtos: omnichannelRequestBody?.gtins ?? [],
      periodoSemanas: 12,
      tipoGtin: 'TODOS',
      redes: [],
      cidade: [],
      uf: [],
    },
    resolver: zodResolver(omnichannelFormSchema),
  });
  const { handleSubmit } = methods;

  const onSubmit = () => {
    const { omnichannelRequestBody } = useOmnichannelStore.getState();
    handleGetOmnichannelGaugeCharts(omnichannelRequestBody);
    handleDetailsPageChange(1, 10);
  };

  return (
    <Container className="main-container modulo-omnichannel">
      <div id="container">
        <Header
          productTitle="InfoPanel"
          title="Módulo Omnichannel"
          subtitle="Analise e compare os preços de cada produto entre todos os canais"
          badgeLabel="Pro"
          textButton="Entenda como funciona"
          onClickTextButton={() => setOmnichannelState('showModalUnderstandBetterOmnichannel', true)}
          icon={
            <Icon asChild>
              <MdHub style={{ height: '20px', width: '20px' }} />
            </Icon>
          }
        />
        <div className={styles['main-content']}>
          {HAS_NO_PERMISSION && (
            <PageBlocker
              id="omnichannel-blocker-btn"
              className={styles['page-blocker-aderencia']}
              title={PAGE_BLOCKER.title}
              subtitle={PAGE_BLOCKER.subtitle}
              buttonText="quero o teste grátis"
              onClick={() => setOmnichannelState('showModalSpeakWithSpecialistOmnichannel', true)}
            />
          )}
          <FormProvider {...methods}>
            <div style={{ position: 'relative' }}>
              <LoadingSpinerLocal loading={isLoadingOmnichannelDetails} position="absolute" size="md" />
              <FiltersArea />
              <ResultsButton onClick={handleSubmit(onSubmit)} hasDataRendered={Boolean(omnichannelDetailsData?.length)} />
            </div>
          </FormProvider>
          {omnichannelDetailsData?.length > 0 ? (
            <>
              <ComparingChannelHeader />
              <div className={styles['gauge-charts-container']}>
                <ElementsRelatedToPhysicalChannelChart
                  title="Sites/Apps próprios em relação ao canal físico"
                  data={omnichannelGaugeChartsData.appsProprios}
                  isLoading={omnichannelGaugeChartsLoading}
                />
                <ElementsRelatedToPhysicalChannelChart
                  title="Apps de entrega em relação ao canal físico"
                  data={omnichannelGaugeChartsData.appsEntrega}
                  isLoading={omnichannelGaugeChartsLoading}
                />
              </div>
              <Detailing loading={isLoadingOmnichannelDetails} />
            </>
          ) : null}
        </div>
      </div>
      <ModalSpeakWithSpecialist
        show={showModalSpeakWithSpecialistOmnichannel}
        title={SPEAK_WITH_SPECIALIST_TITLE.title}
        subtitle={SPEAK_WITH_SPECIALIST_TITLE.subtitle}
        onCancel={() => setOmnichannelState('showModalSpeakWithSpecialistOmnichannel', false)}
      />
      <ModalUnderstandBetter show={showModalUnderstandBetterOmnichannel} onCancel={() => setOmnichannelState('showModalUnderstandBetterOmnichannel', false)} />
    </Container>
  );
};
